/**
 * Client-side attribution capture.
 *
 * Captures UTM parameters and referrer on first landing in a browser
 * session, persists them in localStorage, then exposes a single
 * `getAttribution()` helper used by the visitor tracker and the
 * waitlist form so signups can be attributed to the social platform /
 * campaign that brought the user in.
 */

const STORAGE_KEY = "rt_attribution";

export type Attribution = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  landing_path: string | null;
  platform: string | null;
  captured_at: string;
};

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

/** Map a UTM source or referrer host to a friendly platform name. */
export function classifyPlatform(
  utmSource: string | null | undefined,
  referrer: string | null | undefined,
): string | null {
  const s = (utmSource || "").toLowerCase().trim();
  const utmMap: Record<string, string> = {
    instagram: "Instagram",
    ig: "Instagram",
    "instagram.com": "Instagram",
    facebook: "Facebook",
    fb: "Facebook",
    meta: "Facebook",
    "facebook.com": "Facebook",
    pinterest: "Pinterest",
    pin: "Pinterest",
    "pinterest.com": "Pinterest",
    tiktok: "TikTok",
    tt: "TikTok",
    "tiktok.com": "TikTok",
    youtube: "YouTube",
    yt: "YouTube",
    "youtube.com": "YouTube",
    "youtu.be": "YouTube",
    linkedin: "LinkedIn",
    li: "LinkedIn",
    "linkedin.com": "LinkedIn",
    twitter: "X (Twitter)",
    x: "X (Twitter)",
    "twitter.com": "X (Twitter)",
    "x.com": "X (Twitter)",
    threads: "Threads",
    "threads.net": "Threads",
    reddit: "Reddit",
    "reddit.com": "Reddit",
  };
  if (s && utmMap[s]) return utmMap[s];

  if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      if (/(^|\.)instagram\./.test(host)) return "Instagram";
      if (/(^|\.)(facebook|fb)\./.test(host)) return "Facebook";
      if (/(^|\.)pinterest\./.test(host)) return "Pinterest";
      if (/(^|\.)tiktok\./.test(host)) return "TikTok";
      if (/(^|\.)(youtube|youtu)\./.test(host)) return "YouTube";
      if (/(^|\.)linkedin\./.test(host)) return "LinkedIn";
      if (/(^|\.)(twitter|x)\./.test(host) || host === "t.co")
        return "X (Twitter)";
      if (/(^|\.)threads\./.test(host)) return "Threads";
      if (/(^|\.)reddit\./.test(host)) return "Reddit";
    } catch {
      /* ignore */
    }
  }
  return null;
}

function parseQuery(): Partial<Record<(typeof UTM_KEYS)[number], string>> {
  try {
    const params = new URLSearchParams(window.location.search);
    const out: Record<string, string> = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) out[k] = v.slice(0, 200);
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Initialize attribution. Safe to call on every navigation — it only
 * persists the first time a session lands, but always upgrades when a
 * new UTM is present in the URL.
 */
export function captureAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  const utm = parseQuery();
  const referrer = document.referrer || null;
  const hasIncoming = Object.keys(utm).length > 0;

  let existing: Attribution | null = null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) existing = JSON.parse(raw) as Attribution;
  } catch {
    /* ignore */
  }

  // First visit: capture everything we know.
  if (!existing) {
    const next: Attribution = {
      utm_source: utm.utm_source ?? null,
      utm_medium: utm.utm_medium ?? null,
      utm_campaign: utm.utm_campaign ?? null,
      utm_content: utm.utm_content ?? null,
      utm_term: utm.utm_term ?? null,
      referrer,
      landing_path: window.location.pathname,
      platform: classifyPlatform(utm.utm_source ?? null, referrer),
      captured_at: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    return next;
  }

  // Existing attribution: only overwrite when a fresh UTM arrives.
  if (hasIncoming) {
    const next: Attribution = {
      ...existing,
      utm_source: utm.utm_source ?? existing.utm_source,
      utm_medium: utm.utm_medium ?? existing.utm_medium,
      utm_campaign: utm.utm_campaign ?? existing.utm_campaign,
      utm_content: utm.utm_content ?? existing.utm_content,
      utm_term: utm.utm_term ?? existing.utm_term,
      platform:
        classifyPlatform(utm.utm_source ?? null, referrer) ??
        existing.platform,
      captured_at: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    return next;
  }

  return existing;
}

export function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Attribution;
  } catch {
    /* ignore */
  }
  return null;
}
